import React, { useEffect, useMemo } from 'react';
import { Stack } from 'new/elements/Stack';

import {
  useVendorBuilder,
  VendorType,
} from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';

interface ConfigAWSProps {
  index: number;
}

const defaultParamNames = (vendor: VendorType) => [
  vendor.name + '.aws_access_key',
  vendor.name + '.aws_secret_key',
  vendor.name + '.aws_region',
  vendor.name + '.aws_service',
  vendor.name + '.aws_session_token',
];

const defaultParams = (vendor: VendorType) => [
  {
    name: vendor.name + '.aws_access_key',
    display_name: 'AWS Access Key',
    description:
      'The access key id of the Access Key pair that is used for verifying the identity of the requesting user.',
    order: 1,
    data_type: 'string',
    config: {
      required: true,
    },
  },
  {
    name: vendor.name + '.aws_secret_key',
    display_name: 'AWS Secret Key',
    description:
      'The access secret of the Access Key pair that is used for verifying the identity of the requesting user.',
    order: 2,
    data_type: 'string',
    config: {
      required: true,
      type: 'password',
    },
    secured: true,
  },
  {
    name: vendor.name + '.aws_region',
    display_name: 'AWS Region',
    description: 'The region that is receiving the request.',
    order: 3,
    data_type: 'string',
    config: {
      required: true,
    },
  },
  {
    name: vendor.name + '.aws_service',
    display_name: 'Service Name',
    description: 'The service that is receiving the request.',
    order: 4,
    data_type: 'string',
    config: {
      required: true,
    },
  },
  {
    name: vendor.name + '.aws_session_token',
    display_name: 'Session Token',
    description:
      'Optional session token if the request requires temporary security credentials. This is added to the request as `x-amz-access-token` request header.',
    order: 5,
    data_type: 'string',
    config: {
      required: true,
    },
    secured: true,
  },
];

const fields = (vendor, index) => [
  {
    type: 'text',
    label: (
      <>
        AWS Access Key <code>config["aws.access.key"]</code>
      </>
    ),
    required: true,
    defaultValue: `\$\{${vendor.name}.aws_access_key}`,
    helperText:
      'The access key id of the Access Key pair that is used for verifying the identity of the requesting user.',
    name: `auth_templates[${index}].config["aws.access.key"]`,
  },
  {
    type: 'text',
    label: (
      <>
        AWS Secret Key <code>config["aws.secret.key"]</code>
      </>
    ),
    required: true,
    defaultValue: `\$\{${vendor.name}.aws_secret_key'}`,
    helperText:
      'The access secret of the Access Key pair that is used for verifying the identity of the requesting user.',
    name: `auth_templates[${index}].config["aws.secret.key"]`,
  },
  {
    type: 'text',
    required: true,
    label: (
      <>
        AWS Region <code>config["aws.region"]</code>
      </>
    ),
    defaultValue: `\$\{${vendor.name}.aws_region'}`,
    name: `auth_templates[${index}].config["aws.region"]`,
  },
  {
    type: 'text',
    required: true,
    label: (
      <>
        AWS Service Name <code>config["aws.service"]</code>
      </>
    ),
    defaultValue: `\$\{${vendor.name}.aws_service'}`,
    name: `auth_templates[${index}].config["aws.service"]`,
  },
  {
    type: 'text',
    label: (
      <>
        AWS Session Token <code>config["aws.session.token"]</code>
      </>
    ),
    defaultValue: `\$\{${vendor.name}.aws_session_token'}`,
    helperText:
      'Optional session token if the request requires temporary security credentials. This is added to the request as x-amz-access-token request header.',
    name: `auth_templates[${index}].config["aws.session.token"]`,
  },
];

export const ConfigAWS = (props: ConfigAWSProps) => {
  const { index } = props;
  const { onChange, updateVendor, vendor } = useVendorBuilder();

  useEffect(() => {
    updateVendor((newVendor) => {
      // Adding params
      const params = defaultParams(vendor);
      newVendor.auth_templates[index]?.params.push(...params);
    });

    return () => {
      updateVendor((newVendor) => {
        const params = newVendor.auth_templates[index].params;
        defaultParamNames(newVendor).forEach((name) => {
          const indexToRemove = params.findIndex((param) => param.name === name);
          if (indexToRemove >= 0) {
            params.splice(indexToRemove, 1);
          }
        });
      });
    };
  }, []);

  const awsFields = useMemo(() => fields(vendor, index), [vendor.name, index]);

  return (
    <Stack gap="16px">
      <FormBuilder fields={awsFields} />
    </Stack>
  );
};
